import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Zap, ExternalLink, Gauge } from "lucide-react";

export default function RechargeDialog({ open, onOpenChange }) {
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState("");
  const [subRoom, setSubRoom] = useState("");
  const [meter, setMeter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [buyUrl, setBuyUrl] = useState("https://www.apspdcl.in/");

  useEffect(() => {
    if (!open) return;
    setRoom("");
    setSubRoom("");
    setMeter(null);
    api.get("/meters/rooms").then((r) => setRooms(r.data || [])).catch(() => {});
    api
      .get("/electricity/config")
      .then((r) => r.data?.buy_electricity_url && setBuyUrl(r.data.buy_electricity_url))
      .catch(() => {});
  }, [open]);

  const currentRoom = useMemo(
    () => rooms.find((r) => r.room_number === room),
    [rooms, room],
  );

  const canLookup = room && (!(currentRoom?.sub_rooms?.length > 1) || subRoom);

  const runLookup = async () => {
    setLoading(true);
    setMeter(null);
    try {
      const { data } = await api.post("/meters/lookup", {
        room_number: room,
        sub_room: subRoom || (currentRoom?.sub_rooms?.[0] ?? ""),
      });
      setMeter(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No meter found for that room / sub-room");
    } finally {
      setLoading(false);
    }
  };

  const openBuy = () => {
    if (!meter?.meter_number) return;
    // Open the recharge portal FIRST so the click-initiated window.open is not
    // blocked by browsers (clipboard write is async and can delay the call).
    const win = window.open(buyUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      toast.warning(`Popup blocked — open ${buyUrl} manually. Your meter: ${meter.meter_number}`);
      return;
    }
    // Copy meter number to clipboard (best-effort — silent if denied)
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(meter.meter_number)
        .then(() => toast.success(`Meter ${meter.meter_number} copied — paste it on the recharge site`))
        .catch(() => toast.info(`Your meter number is ${meter.meter_number}`));
    } else {
      toast.info(`Your meter number is ${meter.meter_number}`);
    }
  };

  const subRoomOptions = currentRoom?.sub_rooms?.filter((s) => s !== "") || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg border-2 border-[#0A0A0A] shadow-brutal rounded-xl bg-white"
        data-testid="recharge-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2.5 border-2 border-[#0A0A0A] rounded-xl bg-[#FFE600]">
              <Zap size={22} strokeWidth={2.5} />
            </div>
            <div>
              <DialogTitle className="font-heading font-black text-2xl tracking-tighter">
                Recharge Electricity
              </DialogTitle>
              <DialogDescription className="text-sm font-medium text-[#4A4A4A]">
                Pick your room and sub-room to view your prepaid meter number.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest mb-1.5">
              Room Number
            </label>
            <Select
              value={room}
              onValueChange={(v) => {
                setRoom(v);
                setSubRoom("");
                setMeter(null);
              }}
            >
              <SelectTrigger
                data-testid="recharge-room-select"
                className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-bold"
              >
                <SelectValue placeholder="Select room" />
              </SelectTrigger>
              <SelectContent className="border-2 border-[#0A0A0A]">
                {rooms.map((r) => (
                  <SelectItem key={r.room_number} value={r.room_number}>
                    {r.room_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {subRoomOptions.length > 0 && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-1.5">
                Sub Room
              </label>
              <Select
                value={subRoom}
                onValueChange={(v) => {
                  setSubRoom(v);
                  setMeter(null);
                }}
              >
                <SelectTrigger
                  data-testid="recharge-subroom-select"
                  className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-bold"
                >
                  <SelectValue placeholder="Select sub room" />
                </SelectTrigger>
                <SelectContent className="border-2 border-[#0A0A0A]">
                  {subRoomOptions.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <button
            onClick={runLookup}
            disabled={!canLookup || loading}
            data-testid="recharge-lookup-button"
            className="w-full inline-flex items-center justify-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#00E5FF] shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-50"
          >
            <Gauge size={16} strokeWidth={2.5} />
            {loading ? "Looking up..." : "Show my meter number"}
          </button>

          {meter && (
            <div
              data-testid="recharge-meter-result"
              className="border-2 border-[#0A0A0A] rounded-xl bg-[#FFE600] p-5"
              style={{ boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)" }}
            >
              <div className="text-xs font-bold uppercase tracking-[0.2em]">
                Prepaid Meter Number
              </div>
              <div
                className="font-heading font-black text-3xl tracking-tighter mt-1"
                data-testid="recharge-meter-number"
              >
                {meter.meter_number}
              </div>
              <div className="text-xs font-medium mt-1 text-[#4A4A4A]">
                Room {meter.room_number}
                {meter.sub_room ? ` · Sub ${meter.sub_room}` : ""}
              </div>
              <a
                href={buyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="recharge-fallback-link"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold underline decoration-2 underline-offset-2"
              >
                Or open the recharge portal directly →
              </a>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4 gap-3">
          <button
            onClick={() => onOpenChange(false)}
            data-testid="recharge-cancel"
            className="px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
          >
            Close
          </button>
          <button
            onClick={openBuy}
            disabled={!meter?.meter_number}
            data-testid="recharge-buy-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-50"
          >
            <ExternalLink size={16} strokeWidth={2.5} /> Recharge Options
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
